import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Modal as BaseModal } from 'react-responsive-modal';
import { NotificationManager } from 'react-notifications';

// Components
import AutoCompleteField from '@components/AutoCompleteV2';
import Input from '@components/Input';
import Button from '@components/Button';

// Context
import UserContext from '@components/context/userContext';

// Services
import productsService from '@services/products';
import categoriesService from '@services/categories';
import taxService from '@services/tax';
import { getTaxSelectLabel } from '@utils/taxLabel';

import 'react-responsive-modal/styles.css';
import './BarcodeProductModal.css';

const BarcodeProductModal = ({ 
  isOpen, 
  onClose, 
  barcode, 
  onProductSelected,
  onProductCreated 
}) => {
  const profile = useContext(UserContext);
  const newProductButtonRef = useRef(null);
  const [step, setStep] = useState(1); // 1: Associate, 2: Create
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Form data for new product
  const [newProduct, setNewProduct] = useState({
    nome: '',
    idcategory: null,
    iva: null,
    price: '',
    costprice: ''
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load products
      const productsData = await productsService.get(profile.idEmpresaSec);
      setProducts(productsData.map(product => ({
        ...product,
        label: `[${product.idproduct}] ${product.nome}`,
        value: product.idproduct
      })));

      // Load categories
      const categoriesData = await categoriesService.get(profile.idEmpresaSec);
      setCategories(categoriesData.map(category => ({
        ...category,
        label: category.descricaocategoria,
        value: category.idcategoria
      })));

      // Load taxes (IVA)
      const taxesData = await taxService.get();
      setTaxes(taxesData.map(tax => ({
        ...tax,
        label: getTaxSelectLabel(tax),
        value: tax.code
      })));

    } catch (error) {
      console.error('Error loading data:', error);
      NotificationManager.error('Erro ao carregar dados', 'Erro', 5000);
    } finally {
      setLoading(false);
    }
  }, [profile.idEmpresaSec]);

  useEffect(() => {
    if (isOpen) {
      // Remove focus from any active element
      if (document.activeElement && document.activeElement.blur) {
        document.activeElement.blur();
      }
      
      setStep(1);
      setSelectedProduct(null);
      setNewProduct({
        nome: '',
        idproduct: barcode || '', // Set idproduct to barcode by default
        idcategory: null,
        iva: null,
        price: '',
        costprice: ''
      });
      loadData();
      
      // Focus on close button after modal opens
      setTimeout(() => {
        if (newProductButtonRef.current) {
          newProductButtonRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen, barcode, loadData]);

  const handleAssociateProduct = () => {
    if (!selectedProduct) {
      NotificationManager.warning('Por favor, selecione um produto', 'Aviso', 3000);
      return;
    }

    // Add barcode to selected product
    addBarcodeToProduct(selectedProduct.idproduct, barcode);
  };

  const addBarcodeToProduct = async (productId, barcode) => {
    try {
      setLoading(true);
      const response = await productsService.setBarcode({
        idproduct: productId,
        barcode: barcode,
        idempresa: profile.idEmpresaSec
      });

      if (response === "NOK") {
        NotificationManager.error('Erro ao associar código de barras', 'Erro', 5000);
      } else {
        NotificationManager.success('Código de barras associado com sucesso!', 'Sucesso', 3000);
        onProductSelected(selectedProduct);
        onClose();
      }
    } catch (error) {
      console.error('Error adding barcode:', error);
      NotificationManager.error('Erro ao associar código de barras', 'Erro', 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async () => {
    if (!newProduct.nome || !newProduct.idcategory || !newProduct.iva) {
      NotificationManager.warning('Por favor, preencha todos os campos obrigatórios', 'Aviso', 3000);
      return;
    }

    try {
      setLoading(true);
      const response = await productsService.set({
        idproduct: newProduct.idproduct,
        idempresa: profile.idEmpresaSec,
        name: newProduct.nome,
        idcategory: newProduct.idcategory.value,
        iva: newProduct.iva.value,
        price: parseFloat(newProduct.price) || 0,
        costprice: parseFloat(newProduct.costprice) || 0,
        barcode: barcode,
        ativo: 't',
        mov_stock: 't'
      });

      if (response && response.idproduct) {
        await productsService.setPrices({
          idempresa: profile.idEmpresaSec,
          idproduct: newProduct.idproduct,
          same: true,
          value: {
            idposto: -1,
            price: parseFloat(newProduct.price) || 0,
            costprice: parseFloat(newProduct.costprice) || 0,
            iva: newProduct.iva.value,
            sync: 't',
          },
        });

        if (barcode !== newProduct.idproduct) {
          await productsService.setBarcode({
            idempresa: profile.idEmpresaSec,
            idproduct: newProduct.idproduct,
            barcode: barcode,
          });
        }

        const createdProduct = {
          idproduct: response.idproduct,
          nome: newProduct.nome,
          label: `[${response.idproduct}] ${newProduct.nome}`,
          value: response.idproduct
        };
        
        NotificationManager.success('Produto criado e código de barras associado!', 'Sucesso', 3000);
        onProductCreated(createdProduct);
        onClose();
      } else {
        NotificationManager.error('Erro ao criar produto', 'Erro', 5000);
      }
    } catch (error) {
      console.error('Error creating product:', error);
      NotificationManager.error('Erro ao criar produto', 'Erro', 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleNewProductChange = (field, value) => {
    setNewProduct(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <BaseModal
      open={isOpen}
      onClose={onClose}
      center
      classNames={{
        modal: 'barcode-product-modal'
      }}
    >
      <div className="barcode-product-modal-content">
        <h2>Associar Código de Barras</h2>
        <p className="barcode-info">CB: <strong>{barcode}</strong></p>

        {step === 1 && (
          <div className="associate-step">
            <h3>Associar a Produto Existente</h3>
            <div className="form-group">
              <AutoCompleteField
                options={products}
                value={selectedProduct}
                onChange={(_, product) => setSelectedProduct(product)}
                getOptionLabel={(option) => option.label || ""}
                searchableFields={['nome', 'idproduct']}
                label="Selecionar Produto"
                placeholder="Digite o nome ou ID do produto..."
              />
            </div>
            <div className="modal-actions">
              <Button
                type="primary"
                action={() => setStep(2)}
                title="Criar Novo Produto"
                ref={newProductButtonRef}
              />
              <Button
                type="success"
                action={handleAssociateProduct}
                disabled={loading || !selectedProduct}
                title={loading ? 'Associando...' : 'Associar'}
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="create-step">
            <h3>Criar Novo Produto</h3>
            <div className="form-group">
              <Input
                title="Código do Produto"
                inputType="text"
                name="idproduct"
                value={newProduct.idproduct}
                handleChange={(e) => handleNewProductChange('idproduct', e.target.value)}
                placeholder="Digite o Código do produto..."
                required
              />
            </div>
            <div className="form-group">
              <Input
                title="Nome do Produto"
                inputType="text"
                name="nome"
                value={newProduct.nome}
                handleChange={(e) => handleNewProductChange('nome', e.target.value)}
                placeholder="Digite o nome do produto..."
                required
              />
            </div>
            <div className="form-group">
              <AutoCompleteField
                options={categories}
                value={newProduct.idcategory}
                onChange={(_, category) => handleNewProductChange('idcategory', category)}
                getOptionLabel={(option) => option.label || ""}
                searchableFields={['descricaocategoria']}
                label="Família/Categoria"
                placeholder="Selecione uma categoria..."
                required
              />
            </div>
            <div className="form-group">
              <AutoCompleteField
                options={taxes}
                value={newProduct.iva}
                onChange={(_, tax) => handleNewProductChange('iva', tax)}
                getOptionLabel={(option) => option.label || ""}
                searchableFields={['descricao', 'taxa']}
                label="IVA"
                placeholder="Selecione o IVA..."
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <Input
                  title="Preço de Venda (€)"
                  inputType="number"
                  name="price"
                  value={newProduct.price}
                  handleChange={(e) => handleNewProductChange('price', e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
              <div className="form-group">
                <Input
                  title="Preço de Custo (€)"
                  inputType="number"
                  name="costprice"
                  value={newProduct.costprice}
                  handleChange={(e) => handleNewProductChange('costprice', e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
            </div>
            <div className="modal-actions">
              <Button
                type="success"
                action={handleCreateProduct}
                disabled={loading || !newProduct.idproduct}
                title={loading ? 'Criando...' : 'Criar Produto'}
              />
              <Button
                type="secondary"
                action={() => setStep(1)}
                title="Voltar"
              />
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
};

export default BarcodeProductModal;
